/**
 * 学练考 / 作业 —— 取数入口。
 *
 * 目前返回本地演示数据（页面先把视觉与交互跑通）；
 * 接后端时**只改这一个文件**，把每个 fetchXxx 换成真实请求即可，
 * 页面侧不用动（返回结构保持不变）。
 *
 * 数据口径：
 *   学 = 知识点掌握度（%）        → knowledge: { done, total }
 *   练 = 练习正确率（%）+ 题量     → practice:  { count, rate }，没有练习记录时为 null
 *   考 = 最近一次成绩 + 升降       → exam:      { score, full, delta, term, rank, avg, trend }
 *   作业                          → homework:  { pending }
 */

/* ---------------- 科目外观 ---------------- */

const SUBJECT_META = {
    chinese: { short: '语', bg: '#FCEBEB', fg: '#A32D2D' },
    math: { short: '数', bg: '#E6F1FB', fg: '#185FA5' },
    english: { short: '英', bg: '#EEEDFE', fg: '#534AB7' },
    physics: { short: '物', bg: '#E1F5EE', fg: '#0F6E56' },
    chemistry: { short: '化', bg: '#FAEEDA', fg: '#854F0B' },
    biology: { short: '生', bg: '#EAF3DE', fg: '#3B6D11' },
    history: { short: '史', bg: '#F1EFE8', fg: '#5F5E5A' },
    politics: { short: '政', bg: '#F1EFE8', fg: '#5F5E5A' },
    geography: { short: '地', bg: '#E1F5EE', fg: '#0F6E56' }
};

const FALLBACK_META = { short: '科', bg: '#F1EFE8', fg: '#5F5E5A' };

/** 科目徽章（短名 + 配色） */
export function subjectMeta(key) {
    return SUBJECT_META[key] || FALLBACK_META;
}

/** 掌握度色阶：≥85 稳、70~84 一般、<70 要补 */
export function masteryTone(value) {
    if (value === null || value === undefined) {
        return 'none';
    }
    if (value >= 85) {
        return 'good';
    }
    if (value >= 70) {
        return 'mid';
    }
    return 'low';
}

/* ---------------- 作业状态 ---------------- */

const HOMEWORK_STATUS = {
    // weight 用于排序：越小越靠前（逾期最扎眼）
    overdue: { key: 'overdue', text: '已逾期', bg: '#FCEBEB', fg: '#A32D2D', weight: 0 },
    pending: { key: 'pending', text: '未提交', bg: '#FAEEDA', fg: '#854F0B', weight: 1 },
    correct: { key: 'correct', text: '待订正', bg: '#E6F1FB', fg: '#185FA5', weight: 2 },
    done: { key: 'done', text: '已提交', bg: '#E1F5EE', fg: '#0F6E56', weight: 3 }
};

export function homeworkStatus(key) {
    return HOMEWORK_STATUS[key] || HOMEWORK_STATUS.pending;
}

/** 未交（含待订正）都算「待办」 */
export function isTodo(item) {
    return item.status === 'pending' || item.status === 'correct';
}

/* ---------------- 演示数据 ---------------- */

const OVERVIEW = {
    weekStudyHours: 6.5, // 本周学习时长
    weekStudyDelta: 1.2, // 比上周多的小时
    weekPracticeCount: 132, // 本周练习题量
    weekPracticeRate: 86, // 本周练习正确率
    latestExamAvg: 88, // 最近一次考试各科得分均分（= 528 / 6，与下方 SUBJECTS 对齐）
    latestExamDelta: 0.8 // 比上次多几分（87.2 → 88.0）
};

const SUBJECTS = [
    {
        key: 'chinese',
        name: '语文',
        mastery: 78,
        knowledge: { done: 32, total: 41 },
        practice: { count: 132, rate: 86 },
        exam: { score: 92, full: 120, delta: 3, term: '期中', rank: 6, avg: 89.3, trend: [84, 90, 92] },
        homework: { pending: 1 }
    },
    {
        key: 'math',
        name: '数学',
        mastery: 64,
        knowledge: { done: 27, total: 42 },
        practice: { count: 108, rate: 71 },
        exam: { score: 84, full: 120, delta: -5, term: '期中', rank: 12, avg: 86.7, trend: [88, 86, 84] },
        homework: { pending: 1 }
    },
    {
        key: 'english',
        name: '英语',
        mastery: 91,
        knowledge: { done: 45, total: 49 },
        practice: { count: 156, rate: 94 },
        exam: { score: 96, full: 120, delta: 2, term: '期中', rank: 3, avg: 94.3, trend: [92, 94, 96] },
        homework: { pending: 0 }
    },
    {
        key: 'physics',
        name: '物理',
        mastery: 58,
        knowledge: { done: 19, total: 33 },
        practice: { count: 84, rate: 66 },
        exam: { score: 78, full: 100, delta: -2, term: '单元测', rank: 21, avg: 79.0, trend: [80, 79, 78] },
        homework: { pending: 1 }
    },
    {
        key: 'chemistry',
        name: '化学',
        mastery: 72,
        knowledge: { done: 24, total: 33 },
        practice: { count: 96, rate: 80 },
        exam: { score: 88, full: 100, delta: 6, term: '单元测', rank: 9, avg: 85.3, trend: [79, 84, 88] },
        homework: { pending: 0 }
    },
    {
        key: 'history',
        name: '历史',
        mastery: 83,
        knowledge: { done: 30, total: 36 },
        practice: null, // 这科还没有练习记录，页面显示「—」
        exam: { score: 90, full: 100, delta: 0, term: '月考', rank: 5, avg: 90.0, trend: [90, 90, 90] },
        homework: { pending: 0 }
    }
];

/** 科目中文名（从 SUBJECTS 派生，避免两处维护） */
const SUBJECT_NAME = SUBJECTS.reduce((acc, s) => {
    acc[s.key] = s.name;
    return acc;
}, {});

/** 科目中文名，未知科目回退成 key 本身 */
export function subjectName(key) {
    return SUBJECT_NAME[key] || key;
}

const HOMEWORK = {
    today: [
        {
            id: 'h1',
            subject: 'physics',
            title: '力学实验报告',
            due: '昨天 18:00 截止',
            note: '已逾期 1 天',
            status: 'overdue'
        },
        {
            id: 'h2',
            subject: 'chinese',
            title: '课内文言文整理',
            due: '今天 20:00 截止',
            note: '还有 3 小时',
            status: 'pending'
        },
        {
            id: 'h3',
            subject: 'math',
            title: '单元测错题订正',
            due: '今天 22:00 截止',
            note: '老师已批改',
            status: 'correct'
        },
        {
            id: 'h4',
            subject: 'english',
            title: '听力 D3 精听打卡',
            due: '昨天提交',
            note: '得分 94',
            status: 'done'
        },
        {
            id: 'h5',
            subject: 'english',
            title: '单词 Unit5 默写',
            due: '昨天提交',
            note: '得分 88',
            status: 'done'
        },
        {
            id: 'h6',
            subject: 'chemistry',
            title: '方程式配平练习',
            due: '今天提交',
            note: '得分 100',
            status: 'done'
        }
    ],
    week: [
        {
            id: 'h1',
            subject: 'physics',
            title: '力学实验报告',
            due: '昨天 18:00 截止',
            note: '已逾期 1 天',
            status: 'overdue'
        },
        {
            id: 'h2',
            subject: 'chinese',
            title: '课内文言文整理',
            due: '今天 20:00 截止',
            note: '还有 3 小时',
            status: 'pending'
        },
        {
            id: 'h3',
            subject: 'math',
            title: '单元测错题订正',
            due: '今天 22:00 截止',
            note: '老师已批改',
            status: 'correct'
        },
        {
            id: 'h4',
            subject: 'english',
            title: '听力 D3 精听打卡',
            due: '昨天提交',
            note: '得分 94',
            status: 'done'
        },
        {
            id: 'h5',
            subject: 'english',
            title: '单词 Unit5 默写',
            due: '昨天提交',
            note: '得分 88',
            status: 'done'
        },
        {
            id: 'h6',
            subject: 'chemistry',
            title: '方程式配平练习',
            due: '今天提交',
            note: '得分 100',
            status: 'done'
        },
        {
            id: 'w1',
            subject: 'math',
            title: '二次函数专项卷',
            due: '周一提交',
            note: '得分 92',
            status: 'done'
        },
        {
            id: 'w2',
            subject: 'chinese',
            title: '周记一篇',
            due: '周二提交',
            note: '得分 90',
            status: 'done'
        },
        {
            id: 'w3',
            subject: 'physics',
            title: '受力分析小测',
            due: '周三提交',
            note: '得分 76',
            status: 'done'
        }
    ]
};

/* ---------------- 错题本 ---------------- */

/**
 * 错题（来自考试 / 练习 / 默写）。
 *   mine / right 分别是我的答案与正确答案，页面上做对错对比
 *   times 是这道题累计错的次数，用来排优先级
 *   mastered 是「是否已掌握」，页面上的按钮只切这个字段
 */
const WRONG_ITEMS = [
    {
        id: 'q1',
        subject: 'math',
        tag: '二次函数最值',
        from: '十月月考 · 第 18 题',
        date: '10-09',
        stem: '已知抛物线 y = x² - 4x + 3，求它在 0 ≤ x ≤ 3 上的最小值。',
        mine: '3',
        right: '-1',
        times: 3,
        mastered: false,
        analysis:
            '先配方得 (x - 2)² - 1，顶点 x = 2 落在 [0, 3] 内，最小值取顶点值 -1。易错点是把端点 x = 0 代进去得 3，误当成最小值。'
    },
    {
        id: 'q2',
        subject: 'physics',
        tag: '斜面受力分析',
        from: '单元测 · 第 12 题',
        date: '10-02',
        stem: '质量 2 kg 的物体静止在倾角 30° 的斜面上，求它受到的摩擦力大小。（g 取 10 m/s²）',
        mine: '20 N',
        right: '10 N',
        times: 2,
        mastered: false,
        analysis:
            '沿斜面方向受力平衡：f = mg·sin30° = 2 × 10 × 0.5 = 10 N。错在把重力整份当成了沿斜面分量，漏掉了 sin30°。'
    },
    {
        id: 'q3',
        subject: 'chinese',
        tag: '文言实词',
        from: '课堂默写',
        date: '10-08',
        stem: '解释「假舟楫者，非能水也，而绝江河」中「绝」的意思。',
        mine: '断绝',
        right: '横渡',
        times: 2,
        mastered: false,
        analysis:
            '「绝」在文言里有多义：断、隔绝、横渡。这里搭配「江河」，取「横渡」。判断口诀：先看搭配对象，再看上下句结构。'
    },
    {
        id: 'q4',
        subject: 'english',
        tag: '现在完成时',
        from: '听力 D2 精听',
        date: '10-05',
        stem: '选择：I ______ in this school for three years.（A. study  B. studied  C. have studied  D. am studying）',
        mine: 'B',
        right: 'C',
        times: 2,
        mastered: false,
        analysis:
            '「for + 一段时间」且动作延续到现在，用现在完成时 have studied。看到 for / since 就先想完成时。'
    },
    {
        id: 'q5',
        subject: 'physics',
        tag: '串并联判断',
        from: '每日一练 · 第 7 题',
        date: '10-06',
        stem: '两只灯泡串联后接到 6 V 电源上，L1 电阻 4 Ω、L2 电阻 8 Ω，求 L2 两端电压。',
        mine: '6 V',
        right: '4 V',
        times: 1,
        mastered: false,
        analysis:
            '串联电流相同、电压按电阻分：U2 = 6 × 8 / (4 + 8) = 4 V。错在把「串联电流相等」记成了「电压相等」。'
    },
    {
        id: 'q6',
        subject: 'chemistry',
        tag: '方程式配平',
        from: '方程式配平练习',
        date: '10-09',
        stem: '配平：__ Fe + __ O₂ → __ Fe₃O₄',
        mine: '1 / 1 / 1',
        right: '3 / 2 / 1',
        times: 3,
        mastered: true,
        analysis: '以氧为突破口：右边 4 个 O，所以左边 O₂ 取 2；再让左边 Fe 补到 3。口诀：先奇偶，后最小公倍数。'
    },
    {
        id: 'q7',
        subject: 'math',
        tag: '相似三角形对应边',
        from: '九月月考 · 第 21 题',
        date: '09-26',
        stem: '△ABC ∽ △DEF，AB = 6、DE = 9、BC = 8，求 EF。',
        mine: '5.3',
        right: '12',
        times: 1,
        mastered: true,
        analysis:
            '相似比 = 6 : 9 = 2 : 3，所以 EF = 8 × 3 / 2 = 12。这类题写对应关系要先按顶点顺序列出，再代比例。'
    },
    {
        id: 'q8',
        subject: 'history',
        tag: '近代史时间线',
        from: '期中复习小测',
        date: '10-07',
        stem: '下列事件按时间先后排序：①《马关条约》②《南京条约》③《辛丑条约》',
        mine: '①②③',
        right: '②①③',
        times: 1,
        mastered: true,
        analysis:
            '《南京条约》1842、《马关条约》1895、《辛丑条约》1901。记年份比记顺序牢：42 - 95 - 01，两两间隔都在扩大。'
    }
];

/* ---------------- 成绩曲线 ---------------- */

/** 考试场次（按时间从早到晚） */
const SCORE_EXAMS = [
    { key: 'e1', name: '八月摸底', date: '08-29' },
    { key: 'e2', name: '九月月考', date: '09-26' },
    { key: 'e3', name: '十月月考', date: '10-09' },
    { key: 'e4', name: '期中考试', date: '11-06' }
];

const CLASS_SIZE = 42;

/**
 * 成绩序列。scores 与 SCORE_EXAMS 一一对应，顺序不能乱。
 * 各科最后三次要和首页 SUBJECTS 里的 exam.trend 保持一致。
 */
const SCORE_SERIES = [
    { key: 'total', name: '总分', full: 660, scores: [502, 513, 523, 528], ranks: [11, 8, 5, 3] },
    { key: 'ysw', name: '语数外', full: 360, scores: [258, 264, 270, 272], ranks: [13, 9, 6, 4] },
    { key: 'chinese', name: '语文', full: 120, scores: [79, 84, 90, 92], ranks: [18, 13, 8, 6] },
    { key: 'math', name: '数学', full: 120, scores: [90, 88, 86, 84], ranks: [7, 9, 10, 12] },
    { key: 'english', name: '英语', full: 120, scores: [89, 92, 94, 96], ranks: [8, 6, 4, 3] },
    { key: 'physics', name: '物理', full: 100, scores: [82, 80, 79, 78], ranks: [15, 18, 19, 21] },
    { key: 'chemistry', name: '化学', full: 100, scores: [74, 79, 84, 88], ranks: [22, 16, 11, 9] },
    { key: 'history', name: '历史', full: 100, scores: [88, 90, 90, 90], ranks: [7, 6, 6, 5] }
];

/* ---------------- 作业详情 ---------------- */

/**
 * 作业详情补充内容（键 = 作业 id）。
 * 没在这里配置的作业，会按列表里的信息自动补一份通用详情，不会出现「点进去空白」。
 */
const HOMEWORK_DETAIL = {
    h1: {
        dueAt: '10-11 18:00',
        leftText: '已逾期 1 天',
        level: 'overdue',
        remind: '老师已提醒过 1 次，尽快补交',
        require: [
            '用打点计时器测出小车做匀加速直线运动的加速度，至少记录 5 组数据。',
            '根据数据画出 v–t 图像，由图像斜率求加速度，并说明主要误差来源。',
            '报告包含实验目的、器材、步骤、数据表格、图像、结论，不少于 600 字。'
        ],
        attach: [{ name: '力学实验记录表.xlsx', size: '38 KB' }],
        submit: null
    },
    h2: {
        dueAt: '今天 20:00',
        leftText: '还有 3 小时',
        level: 'pending',
        require: [
            '整理《劝学》《师说》两篇中的通假字、古今异义词，各不少于 8 组。',
            '每一条都要抄原句，并在句后标注出处与释义。',
            '用作业本手写后拍照上传，字迹要清楚。'
        ],
        attach: [],
        submit: null
    },
    h3: {
        dueAt: '今天 22:00',
        leftText: '待订正',
        level: 'correct',
        require: ['把单元测中批红的题目在错题本上重做一遍，写明错因。', '订正后拍照上传，标注题号。'],
        attach: [{ name: '单元测答卷（扫描）.pdf', size: '1.1 MB' }],
        submit: {
            time: '今天 19:40',
            files: [{ name: '错题订正.jpg', size: '820 KB' }],
            score: 84,
            full: 120,
            teacher: '王老师',
            comment: '整体思路对了，二次函数最值那道还是漏了顶点，订正时把定义域写清楚。',
            fixes: ['第 18 题 二次函数在闭区间上的最值：需先判断顶点是否落在区间内', '第 22 题 分式方程忘记验根']
        }
    },
    h4: {
        dueAt: '10-11 21:00',
        leftText: '已提交',
        level: 'done',
        require: ['精听 D3 音频两遍，逐句跟读并标注连读、弱读位置。', '提交听写文本与跟读录音。'],
        attach: [{ name: '听力音频 D3.mp3', size: '2.4 MB' }],
        submit: {
            time: '10-11 20:12',
            files: [
                { name: '听写文本.jpg', size: '640 KB' },
                { name: '跟读录音.m4a', size: '1.8 MB' }
            ],
            score: 94,
            full: 100,
            teacher: '李老师',
            comment: '辨识度比上次好，第 4、7 句的连读再细一点，语速可以再跟紧一些。',
            fixes: []
        }
    },
    h6: {
        dueAt: '10-12 20:00',
        leftText: '已提交',
        level: 'done',
        require: ['完成 12 道方程式配平，用最小公倍数法写出过程。'],
        attach: [],
        submit: {
            time: '今天 18:05',
            files: [{ name: '配平练习.jpg', size: '760 KB' }],
            score: 100,
            full: 100,
            teacher: '陈老师',
            comment: '全对，过程写得也规范，继续保持。',
            fixes: []
        }
    }
};

/* ---------------- 取数接口 ---------------- */

/** 模拟网络延迟；接真实接口时这块直接删掉 */
const MOCK_DELAY = 0;

function respond(data) {
    if (!MOCK_DELAY) {
        return Promise.resolve(data);
    }
    return new Promise(resolve => {
        setTimeout(() => resolve(data), MOCK_DELAY);
    });
}

/** 顶部「学练考」总览 */
export function fetchStudyOverview() {
    return respond(Object.assign({}, OVERVIEW));
}

/**
 * 各科学练考。
 * 只返回**有数据的科目**：掌握度和成绩都没有的科目不进列表。
 */
export function fetchSubjects() {
    const list = SUBJECTS.filter(s => s.mastery !== null && s.mastery !== undefined).map(s =>
        Object.assign({}, s, {
            meta: subjectMeta(s.key),
            masteryTone: masteryTone(s.mastery)
        })
    );
    return respond(list);
}

/** 作业列表：range = 'today' | 'week'，按紧急度排序 */
export function fetchHomework(range) {
    const src = HOMEWORK[range] || HOMEWORK.today;
    const list = src
        .map(it =>
            Object.assign({}, it, {
                meta: subjectMeta(it.subject),
                statusMeta: homeworkStatus(it.status)
            })
        )
        .sort((a, b) => a.statusMeta.weight - b.statusMeta.weight);
    return respond(list);
}

/** 作业统计：待交（含待订正）/ 已交 / 逾期 */
export function homeworkStats(list) {
    const stat = { todo: 0, done: 0, overdue: 0 };
    (list || []).forEach(it => {
        if (it.status === 'done') {
            stat.done++;
        } else if (it.status === 'overdue') {
            stat.overdue++;
        } else {
            stat.todo++;
        }
    });
    return stat;
}

/* ---------------- 错题本 ---------------- */

/** 错题列表（按错误次数从多到少） */
export function fetchWrongBook() {
    const list = WRONG_ITEMS.map(it =>
        Object.assign({}, it, {
            meta: subjectMeta(it.subject),
            mastered: !!it.mastered
        })
    ).sort((a, b) => b.times - a.times);
    return respond(list);
}

/** 标记 / 取消「已掌握」；接后端时改成 PUT 请求即可 */
export function setWrongMastered(id, mastered) {
    const hit = WRONG_ITEMS.filter(it => it.id === id)[0];
    if (hit) {
        hit.mastered = !!mastered;
    }
    return respond({ id, mastered: !!mastered });
}

/** 错题概览：总数 / 未掌握 / 掌握率 */
export function wrongStats(list) {
    const all = list || [];
    const total = all.length;
    const unmastered = all.filter(it => !it.mastered).length;
    return {
        total,
        unmastered,
        mastered: total - unmastered,
        rate: total ? Math.round(((total - unmastered) / total) * 100) : 0
    };
}

/* ---------------- 成绩曲线 ---------------- */

/** 按科目统计错题数量（用于错题本科目筛选条） */
export function wrongFacets(list) {
    const map = {};
    (list || []).forEach(it => {
        if (!map[it.subject]) {
            map[it.subject] = {
                key: it.subject,
                name: subjectName(it.subject),
                count: 0,
                unmastered: 0
            };
        }
        map[it.subject].count++;
        if (!it.mastered) {
            map[it.subject].unmastered++;
        }
    });
    return Object.keys(map).map(k => map[k]);
}

const SERIES_META = {
    total: { short: '总', bg: '#E6F1FB', fg: '#185FA5' },
    ysw: { short: '主', bg: '#EEEDFE', fg: '#534AB7' }
};

/** 成绩曲线：考试场次 + 各序列（总分 / 语数外 / 各科） */
export function fetchScoreTrend() {
    const exams = SCORE_EXAMS.map(e => Object.assign({}, e));
    const series = SCORE_SERIES.map(s => {
        const last = s.scores.length - 1;
        return Object.assign({}, s, {
            meta: SERIES_META[s.key] || subjectMeta(s.key),
            classSize: CLASS_SIZE,
            latest: s.scores[last],
            delta: last > 0 ? s.scores[last] - s.scores[last - 1] : 0,
            rank: s.ranks[last],
            rankDelta: last > 0 ? s.ranks[last - 1] - s.ranks[last] : 0
        });
    });
    const total = series[0];
    const last = exams.length - 1;
    return respond({
        exams,
        series,
        latest: {
            name: exams[last].name,
            date: exams[last].date,
            score: total.scores[last],
            full: total.full,
            rank: total.ranks[last],
            classSize: CLASS_SIZE,
            delta: total.scores[last] - total.scores[last - 1],
            rankDelta: total.ranks[last - 1] - total.ranks[last]
        }
    });
}

/* ---------------- 作业详情 ---------------- */

/** 从列表里按 id 找出作业（今天 / 本周都找一遍） */
function findHomework(id) {
    const ids = Object.keys(HOMEWORK);
    for (let i = 0; i < ids.length; i++) {
        const hit = HOMEWORK[ids[i]].filter(it => it.id === id)[0];
        if (hit) {
            return hit;
        }
    }
    return null;
}

/** 没单独配置详情的作业，按列表信息补一份通用详情 */
function fallbackDetail(item) {
    const scoreMatch = /(\d+)/.exec(item.note || '');
    const submitted = item.status === 'done';
    return {
        dueAt: item.due,
        leftText: item.note,
        level: item.status,
        remind: '',
        require: ['按老师要求完成，并在截止时间前提交。', '上传的图片或文件要清晰、完整，能看清解题过程。'],
        attach: [],
        submit: submitted
            ? {
                  time: item.due,
                  files: [{ name: item.title + '.jpg', size: '1.2 MB' }],
                  score: scoreMatch ? Number(scoreMatch[1]) : null,
                  full: null,
                  teacher: '任课老师',
                  comment: '已批改，保持这个节奏。',
                  fixes: []
              }
            : null
    };
}

/** 作业详情：找不到 id 时返回 null，页面显示空态 */
export function fetchHomeworkDetail(id) {
    const item = findHomework(id);
    if (!item) {
        return respond(null);
    }
    const merged = Object.assign({}, fallbackDetail(item), HOMEWORK_DETAIL[item.id] || {});
    return respond(
        Object.assign({}, item, merged, {
            meta: subjectMeta(item.subject),
            statusMeta: homeworkStatus(item.status),
            canSubmit: item.status !== 'done'
        })
    );
}
